/**
 * Pure colour-scale resolver for score-cell backgrounds.
 *
 * The wire shape (from the Python `TaskSamplesView.score_color_scales`
 * field) is one of:
 *
 *   - a named palette string (numeric scores) — `"good-high"` etc.
 *   - a value→role map (categorical scores) — `{ "yes": "bad", ... }`
 *
 * Both forms resolve to Bootstrap `*-bg-subtle` CSS variables, so the
 * actual colour the browser paints adapts to light / dark mode without
 * any JS-side colour math. We never produce hex strings — we produce
 * CSS expressions that reference the live theme variables, often via
 * `color-mix()` for smooth interpolation across a numeric range.
 *
 * Future v2 can add an explicit `list[ColorStop]` form for hand-rolled
 * gradients (e.g. "0 transparent → 10 blue") by extending
 * `WireScoreColorScale` and `resolveScale`; the consumer-side
 * `colorForValue` API stays the same.
 */

import { valueAsString } from "@tsmono/util";

export type ScoreColorPalette =
  "good-high" | "good-low" | "neutral" | "diverging";

export type ScoreColorRole = "good" | "bad" | "warn" | "info" | "muted";

/** Numeric scale with optional explicit bounds. The author writes
 *  this when the metric has a known *conceptual* range that may not
 *  match the observed data range — e.g. a 1..10 rubric where this
 *  log only saw values 1..3. */
export interface ScoreColorScaleObject {
  palette: ScoreColorPalette;
  min?: number | null;
  max?: number | null;
}

/** Wire shape — one entry of the `score_color_scales` map. */
export type WireScoreColorScale =
  ScoreColorPalette | ScoreColorScaleObject | Record<string, ScoreColorRole>;

/** Internal resolved form. The `kind` tag tells the renderer how to
 *  pick a colour for a given cell value. */
export type ResolvedScale =
  | {
      kind: "gradient";
      /** Three concrete CSS colour expressions, anchored at
       *  `bounds.min`, `(min+max)/2`, `bounds.max`. */
      low: string;
      mid: string;
      high: string;
      min: number;
      max: number;
    }
  | {
      kind: "categorical";
      /** Pre-baked map from raw score value (string) to a CSS colour
       *  expression. Numbers and booleans are coerced to strings so
       *  authors can map either form. */
      colors: Record<string, string>;
    };

const ROLE_TO_VAR: Record<ScoreColorRole, string> = {
  good: "var(--bs-success-bg-subtle)",
  bad: "var(--bs-danger-bg-subtle)",
  warn: "var(--bs-warning-bg-subtle)",
  info: "var(--bs-info-bg-subtle)",
  muted: "var(--bs-secondary-bg-subtle)",
};

const PALETTE_STOPS: Record<
  ScoreColorPalette,
  { low: string; mid: string; high: string }
> = {
  "good-high": {
    low: "var(--bs-danger-bg-subtle)",
    mid: "var(--bs-warning-bg-subtle)",
    high: "var(--bs-success-bg-subtle)",
  },
  "good-low": {
    low: "var(--bs-success-bg-subtle)",
    mid: "var(--bs-warning-bg-subtle)",
    high: "var(--bs-danger-bg-subtle)",
  },
  neutral: {
    low: "transparent",
    mid: "color-mix(in srgb, var(--bs-info-bg-subtle) 50%, transparent)",
    high: "var(--bs-info-bg-subtle)",
  },
  diverging: {
    low: "var(--bs-danger-bg-subtle)",
    mid: "transparent",
    high: "var(--bs-success-bg-subtle)",
  },
};

const isPaletteName = (s: string): s is ScoreColorPalette =>
  s === "good-high" || s === "good-low" || s === "neutral" || s === "diverging";

// Treat anything with a `palette` key as the object form (even if the
// palette name is invalid — that's better caught in `resolveGradient`
// where we can return null rather than silently falling through to the
// categorical branch and producing a nonsense `{ kind: "categorical",
// colors: { palette: undefined } }` shape).
const isScaleObject = (s: WireScoreColorScale): s is ScoreColorScaleObject =>
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  typeof s === "object" && s !== null && "palette" in s;

/**
 * Lift a wire-shape scale into the internal `ResolvedScale` form.
 *
 * - Named palettes need numeric `bounds` to anchor the gradient.
 *   The wire form `{ palette, min, max }` lets the author override
 *   either bound; missing bounds fall back to the descriptor's
 *   auto-detection. Without any usable range we return null so the
 *   caller leaves the cell unpainted.
 * - Categorical maps don't depend on bounds.
 *
 * Returns null when the scale can't be applied — the caller treats
 * that as "no background".
 */
export function resolveScale(
  scale: WireScoreColorScale,
  bounds: { min?: number; max?: number }
): ResolvedScale | null {
  // String shorthand — palette name only, descriptor bounds.
  if (typeof scale === "string") {
    if (!isPaletteName(scale)) return null;
    return resolveGradient(scale, bounds.min, bounds.max);
  }
  // Object form with explicit palette + optional bounds. Author-supplied
  // bounds win over descriptor's auto-detection so a fixed conceptual
  // range (e.g. 1..10 rubric) doesn't get re-anchored to whatever the
  // observed data happens to be.
  if (isScaleObject(scale)) {
    return resolveGradient(
      scale.palette,
      scale.min ?? bounds.min,
      scale.max ?? bounds.max
    );
  }
  // Categorical: prebake string keys; coerce booleans/numbers at lookup
  // time so authors can write either `{"yes": ...}` or `{"true": ...}`.
  const colors: Record<string, string> = {};
  for (const [value, role] of Object.entries(scale)) {
    colors[value] = ROLE_TO_VAR[role];
  }
  return { kind: "categorical", colors };
}

function resolveGradient(
  palette: ScoreColorPalette,
  min: number | null | undefined,
  max: number | null | undefined
): ResolvedScale | null {
  if (!isPaletteName(palette)) return null;
  if (typeof min !== "number" || typeof max !== "number" || min === max) {
    // Need a real range to interpolate against. Equal min/max would
    // make every value land at the midpoint stop; better to render
    // nothing than a misleading uniform colour.
    return null;
  }
  const stops = PALETTE_STOPS[palette];
  return { kind: "gradient", ...stops, min, max };
}

/**
 * Pick a CSS colour expression for a given score value.
 *
 * Returns undefined when the scale doesn't apply to the value:
 *   - gradient scale on non-numeric / NaN
 *   - categorical scale on a value not in the map
 *
 * The caller (a column's `cellStyle` callback) translates undefined
 * into "no background" so the cell renders transparent.
 */
export function colorForValue(
  scale: ResolvedScale,
  value: unknown
): string | undefined {
  if (scale.kind === "gradient") {
    if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
    // Map value into [0, 1] and interpolate via color-mix(). The
    // browser resolves the underlying CSS variables live, so the
    // result follows the active theme.
    const t = clamp01((value - scale.min) / (scale.max - scale.min));
    if (t <= 0.5) {
      const pct = Math.round((1 - t * 2) * 100);
      return `color-mix(in srgb, ${scale.low} ${pct}%, ${scale.mid})`;
    }
    const pct = Math.round((t - 0.5) * 2 * 100);
    return `color-mix(in srgb, ${scale.high} ${pct}%, ${scale.mid})`;
  }
  // Categorical: stringify scalar values so booleans/numbers can match
  // string-keyed map entries (e.g. `{"true": "good"}`).
  if (value === null || value === undefined) return undefined;
  const key = typeof value === "string" ? value : valueAsString(value);
  return scale.colors[key];
}

const clamp01 = (n: number): number => (n < 0 ? 0 : n > 1 ? 1 : n);
